import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

// Keep the existing direct GoKwik contract, with one checkout launch at a time.
let checkoutInFlight = false;

/** Native scroll snapping, six inert loop buffers, and only one loaded video. */
export class ShoppableReels extends Component {
  requiredRefs = ['track', 'cards', 'status', 'rotation', 'variantDialog', 'variantBody'];
  #variantOpener;
  #abort;
  #observer;
  #resizeObserver;
  #cards = [];
  #buffer = 0;
  #physicalIndex = 0;
  #activeIndex = 0;
  #scrollFrame = 0;
  #resizeFrame = 0;
  #normalizeFrame = 0;
  #settleTimer = 0;
  #rotationTimer = 0;
  #checkoutTimer = 0;
  #checkoutAbort;
  #busy = false;
  #moving = false;
  #normalizing = false;
  #rotationPaused = false;
  #gesture;
  #touching = false;
  #suppressClickUntil = 0;
  #visible = new Set();
  #pausedByUser = new Set();
  #requestedByUser = new Set();
  #playRequests = new WeakMap();
  #motion;
  #connection;

  connectedCallback() {
    super.connectedCallback();
    this.#connect();
  }

  updatedCallback() {
    this.#cleanup();
    super.updatedCallback();
    this.#connect();
  }

  #connect() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.refs.variantDialog.addEventListener('close', this.#variantClosed, { signal });
    this.refs.variantDialog.addEventListener('click', this.#variantBackdrop, { signal });
    this.#motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#connection = navigator.connection;
    this.#buildLoop();
    this.#motion.addEventListener('change', this.#preferencesChanged, { signal });
    this.#connection?.addEventListener('change', this.#syncVideo, { signal });
    window.addEventListener('resize', this.#onResize, { passive: true, signal });
    document.addEventListener('visibilitychange', this.#preferencesChanged, { signal });
    document.addEventListener('shopify:block:select', this.#selectEditorBlock, { signal });
    const track = this.refs.track;
    track.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    track.addEventListener('scrollend', this.#settle, { passive: true, signal });
    track.addEventListener('pointerdown', this.#pointerDown, { passive: true, signal });
    track.addEventListener('touchstart', this.#touchStart, { passive: true, signal });
    window.addEventListener('touchend', this.#touchEnd, { passive: true, signal });
    window.addEventListener('touchcancel', this.#touchEnd, { passive: true, signal });
    window.addEventListener('pointerup', this.#pointerUp, { passive: true, signal });
    window.addEventListener('pointercancel', this.#pointerUp, { passive: true, signal });
    track.addEventListener('wheel', this.#wheel, { passive: true, signal });
    this.addEventListener('focusin', (event) => {
      if (event.target.closest('.psr__product-panel')) this.#pauseRotation();
    }, { signal });

    for (const card of this.#cards) {
      const video = card.querySelector('video');
      if (!video) continue;
      video.muted = true;
      video.addEventListener('playing', () => {
        if (this.#canPlay(card)) this.#showPlayback(card, true);
        else this.#pause(card);
      }, { signal });
      video.addEventListener('pause', () => this.#showPlayback(card, false), { signal });
      video.addEventListener('ended', () => {
        this.#pausedByUser.add(card);
        this.#requestedByUser.delete(card);
        this.#showPlayback(card, false);
      }, { signal });
      video.addEventListener('error', () => {
        if (!video.hasAttribute('src')) return;
        this.#pausedByUser.add(card);
        this.#requestedByUser.delete(card);
        this.#showPlayback(card, false);
        if (card === this.#activeCard) this.refs.status.textContent = this.dataset.videoError;
      }, { signal });
    }

    this.dataset.enhanced = 'true';
    this.refs.navigation.hidden = this.refs.cards.length < 2;
    this.#physicalIndex = this.#buffer + Math.min(this.#activeIndex, this.refs.cards.length - 1);
    this.#activate(this.#physicalIndex);
    this.querySelectorAll('.psr__form').forEach((form) => this.#updateVariant(form));
    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio >= .25) this.#visible.add(entry.target);
        else this.#visible.delete(entry.target);
      }
      this.#syncVideo();
      this.#scheduleRotation();
    }, { rootMargin: '0px', threshold: [0, .25] });
    this.#cards.forEach((card) => this.#observer.observe(card));
    this.#resizeObserver = new ResizeObserver(this.#onResize);
    this.#resizeObserver.observe(track);
    this.#onResize();
  }

  // Three neighbors on each end cover the five-card desktop view during a wrap.
  // Product controls travel with each reel. Buffer IDs remain unique and videos inert.
  #buildLoop() {
    const originals = [...this.refs.cards];
    this.#buffer = originals.length > 1 ? 3 : 0;
    const makeCopy = (index) => {
      const original = originals[(index % originals.length + originals.length) % originals.length];
      const copy = original.cloneNode(true);
      const copiedIds = new Map();
      for (const element of [copy, ...copy.querySelectorAll('*')]) {
        const originalId = element.getAttribute('id');
        if (originalId) {
          const uniqueId = `${originalId}-loop-${index}`;
          copiedIds.set(originalId, uniqueId);
          element.setAttribute('id', uniqueId);
        }
        element.removeAttribute('ref');
        for (const attribute of [...element.attributes]) {
          if (attribute.name.startsWith('data-shopify')) element.removeAttribute(attribute.name);
        }
      }
      copy.querySelectorAll('[for]').forEach((label) => {
        const target = copiedIds.get(label.getAttribute('for'));
        if (target) label.setAttribute('for', target);
      });
      copy.dataset.loopClone = 'true';
      copy.setAttribute('aria-hidden', 'true');
      copy.classList.remove('is-active', 'is-playing');
      copy.querySelectorAll('button, a, select').forEach((control) => { control.tabIndex = -1; });
      const video = copy.querySelector('video');
      if (video) { video.removeAttribute('src'); video.preload = 'none'; }
      return copy;
    };
    const before = Array.from({ length: this.#buffer }, (_, i) => makeCopy(i - this.#buffer));
    const after = Array.from({ length: this.#buffer }, (_, i) => makeCopy(originals.length + i));
    this.refs.track.prepend(...before);
    this.refs.track.append(...after);
    this.#cards = [...before, ...originals, ...after];
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  #cleanup() {
    this.closeVariantDialog();
    this.#abort?.abort();
    this.#checkoutAbort?.abort();
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#scrollFrame);
    cancelAnimationFrame(this.#resizeFrame);
    cancelAnimationFrame(this.#normalizeFrame);
    clearTimeout(this.#settleTimer);
    clearTimeout(this.#rotationTimer);
    clearTimeout(this.#checkoutTimer);
    this.#scrollFrame = this.#resizeFrame = this.#normalizeFrame = 0;
    this.#settleTimer = this.#rotationTimer = this.#checkoutTimer = 0;
    this.#cards.forEach((card) => {
      this.#release(card);
      if (card.dataset.loopClone) card.remove();
    });
    this.#cards = [];
    this.#visible.clear();
    this.#pausedByUser.clear();
    this.#requestedByUser.clear();
    this.#gesture = undefined;
    this.#touching = false;
    this.#moving = this.#normalizing = false;
    if (this.#busy) checkoutInFlight = false;
    this.#busy = false;
  }

  get #activeCard() { return this.#cards[this.#physicalIndex]; }

  #onResize = () => {
    cancelAnimationFrame(this.#resizeFrame);
    this.#resizeFrame = requestAnimationFrame(() => {
      this.#resizeFrame = 0;
      if (!this.isConnected) return;
      clearTimeout(this.#settleTimer);
      const width = this.refs.track.clientWidth;
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const gap = mobile ? 12 : Math.min(Number(this.dataset.cardGap) || 24, width / 20);
      const size = mobile ? width * Math.min(82, Number(this.dataset.mobileWidth) || 70) / 100 : (width - 4 * gap) / 5;
      this.style.setProperty('--psr-card-size', `${Math.max(80, size)}px`);
      this.style.setProperty('--psr-live-gap', `${gap}px`);
      this.#moving = false;
      this.#jump(this.#buffer + this.#activeIndex);
    });
  };

  #jump(index) {
    this.#normalizing = true;
    this.#activate(index);
    this.#scrollToPhysical(index, 'instant');
    cancelAnimationFrame(this.#normalizeFrame);
    this.#normalizeFrame = requestAnimationFrame(() => {
      this.#normalizeFrame = 0;
      this.#normalizing = false;
      this.#syncVideo();
      this.#scheduleRotation();
    });
  }

  #onScroll = () => {
    if (this.#normalizing) return;
    this.#moving = true;
    clearTimeout(this.#rotationTimer);
    this.#rotationTimer = 0;
    this.#syncVideo();
    clearTimeout(this.#settleTimer);
    // Fallback for browsers without scrollend. It never moves the document.
    this.#settleTimer = setTimeout(this.#settle, 180);
    if (this.#scrollFrame) return;
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = 0;
      this.#updateCenter();
    });
  };

  #updateCenter = () => {
    if (!this.isConnected) return;
    const track = this.refs.track.getBoundingClientRect();
    const center = track.left + track.width / 2;
    let nearest = 0;
    let distance = Infinity;
    this.#cards.forEach((card, index) => {
      const rect = card.getBoundingClientRect();
      const delta = Math.abs(rect.left + rect.width / 2 - center);
      if (delta < distance) { distance = delta; nearest = index; }
    });
    if (nearest !== this.#physicalIndex) this.#activate(nearest);
  };

  #settle = () => {
    if (!this.isConnected || this.#normalizing || this.#gesture || this.#touching) return;
    clearTimeout(this.#settleTimer);
    this.#settleTimer = 0;
    this.#updateCenter();
    this.#moving = false;
    const originalIndex = this.#buffer + this.#activeIndex;
    if (this.#physicalIndex !== originalIndex) this.#jump(originalIndex);
    else { this.#syncVideo(); this.#scheduleRotation(); }
  };

  #activate(index) {
    const previous = this.#activeCard;
    this.#physicalIndex = index;
    this.#activeIndex = Number(this.#activeCard.dataset.reelIndex);
    this.#cards.forEach((card, i) => {
      const active = i === index;
      card.classList.toggle('is-active', active);
      card.querySelectorAll('.psr__play, .psr__sound').forEach((button) => { button.tabIndex = active && !card.dataset.loopClone ? 0 : -1; });
      if (!active) {
        if (card === previous) this.#requestedByUser.delete(card);
        this.#release(card);
      }
    });
    this.refs.status.textContent = '';
    this.#syncVideo();
  }

  #scrollToPhysical(index, behavior) {
    const card = this.#cards[Math.max(0, Math.min(index, this.#cards.length - 1))];
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const track = this.refs.track.getBoundingClientRect();
    this.refs.track.scrollTo({
      left: this.refs.track.scrollLeft + rect.left + rect.width / 2 - track.left - track.width / 2,
      behavior: behavior || (this.#motion.matches ? 'instant' : 'smooth'),
    });
  }

  #step(direction) {
    if (this.#moving || this.refs.cards.length < 2) return;
    clearTimeout(this.#rotationTimer);
    this.#rotationTimer = 0;
    this.#scrollToPhysical(this.#physicalIndex + direction);
  }

  previous() { this.#step(-1); }
  next() { this.#step(1); }

  selectReel(event) {
    if (Date.now() < this.#suppressClickUntil) return;
    if (event.target.closest('.psr__product-panel')) { this.#pauseRotation(); return; }
    const card = event.target.closest('[data-reel-index]');
    if (!card) return;
    this.#pauseRotation();
    this.#scrollToPhysical(this.#cards.indexOf(card));
  }

  navigate(event) {
    if (event.target !== this.refs.track) return;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault(); this.#step(event.key === 'ArrowLeft' ? -1 : 1);
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      this.#scrollToPhysical(this.#buffer + (event.key === 'Home' ? 0 : this.refs.cards.length - 1));
    }
  }

  #selectEditorBlock = (event) => {
    const index = this.refs.cards.findIndex((card) => card.dataset.blockId === event.detail?.blockId);
    if (index >= 0) {
      this.#pauseRotation();
      this.#scrollToPhysical(this.#buffer + index);
    }
  };

  #pointerDown = (event) => {
    this.#gesture = { x: event.clientX, y: event.clientY, id: event.pointerId };
    clearTimeout(this.#rotationTimer);
    this.#rotationTimer = 0;
  };

  #pointerUp = (event) => {
    if (!this.#gesture || event.pointerId !== this.#gesture.id) return;
    if (Math.abs(event.clientX - this.#gesture.x) > 10 || Math.abs(event.clientY - this.#gesture.y) > 10 || event.type === 'pointercancel') {
      this.#suppressClickUntil = Date.now() + 450;
    }
    this.#gesture = undefined;
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(this.#settle, 180);
  };

  #wheel = () => {
    clearTimeout(this.#rotationTimer);
    this.#rotationTimer = 0;
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(this.#settle, 180);
  };

  #touchStart = () => {
    this.#touching = true;
    clearTimeout(this.#rotationTimer);
    this.#rotationTimer = 0;
  };

  #touchEnd = (event) => {
    if (event.touches?.length) return;
    this.#touching = false;
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(this.#settle, 180);
  };

  #pauseRotation = () => {
    this.#rotationPaused = true;
    this.#scheduleRotation();
  };

  toggleRotation() {
    this.#rotationPaused = !this.#rotationPaused;
    this.#scheduleRotation();
  }

  #scheduleRotation() {
    clearTimeout(this.#rotationTimer);
    this.#rotationTimer = 0;
    const paused = this.#rotationPaused || this.#motion.matches || this.dataset.autoSlide === 'false';
    this.refs.rotation.setAttribute('aria-pressed', String(paused));
    this.refs.rotation.setAttribute('aria-label', paused ? this.dataset.resumeSlider : this.dataset.pauseSlider);
    this.refs.rotation.querySelector('span').textContent = paused ? this.dataset.resumeSlider : this.dataset.pauseSlider;
    if (paused || !this.isConnected || document.hidden || this.#busy || this.#moving || this.#normalizing || this.#gesture || this.#touching || !this.#visible.has(this.#activeCard) || this.refs.cards.length < 2) return;
    const delay = Math.max(2, Number(this.dataset.slideInterval) || 4) * 1000;
    this.#rotationTimer = setTimeout(() => {
      this.#rotationTimer = 0;
      this.#step(1);
    }, delay);
  }

  #preferencesChanged = () => { this.#syncVideo(); this.#scheduleRotation(); };

  #canPlay(card) {
    if (!this.isConnected || this.refs.variantDialog.open || this.#moving || this.#normalizing || document.hidden || card !== this.#activeCard || !this.#visible.has(card) || this.#pausedByUser.has(card)) return false;
    return this.#requestedByUser.has(card) || (this.dataset.autoplay === 'true' && !this.#motion.matches && !this.#connection?.saveData);
  }

  #syncVideo = () => {
    this.#cards.forEach((card) => {
      if (this.#canPlay(card)) this.#play(card);
      else if (!this.#visible.has(card) || document.hidden || card !== this.#activeCard) this.#release(card);
      else this.#pause(card);
    });
  };

  #source(video) {
    const sources = [...video.querySelectorAll('source[data-src]')].sort((a, b) => Number(a.dataset.width) - Number(b.dataset.width));
    const target = Math.min(720, video.clientWidth * Math.min(window.devicePixelRatio || 1, 1.5));
    return sources.find((source) => Number(source.dataset.width) >= target) || sources.at(-1);
  }

  async #play(card) {
    const video = card.querySelector('video');
    if (!video || !this.#canPlay(card) || this.#playRequests.has(video) || !video.paused) return;
    if (!video.hasAttribute('src')) {
      const source = this.#source(video);
      if (!source) return;
      video.src = source.dataset.src;
      video.load();
    }
    const request = Symbol();
    this.#playRequests.set(video, request);
    try {
      await video.play();
      if (this.#playRequests.get(video) !== request || !this.#canPlay(card)) {
        if (!this.#canPlay(card)) this.#pause(card);
        return;
      }
    } catch (error) {
      if (this.#playRequests.get(video) === request && error.name !== 'AbortError' && this.#requestedByUser.has(card)) this.refs.status.textContent = this.dataset.videoError;
    } finally {
      if (this.#playRequests.get(video) === request) this.#playRequests.delete(video);
    }
  }

  #showPlayback(card, playing) {
    card.classList.toggle('is-playing', playing);
    const button = card.querySelector('.psr__play');
    button.setAttribute('aria-pressed', String(playing));
    button.setAttribute('aria-label', `${playing ? this.dataset.pauseLabel : this.dataset.playLabel} — ${card.dataset.title}`);
  }

  #pause(card) {
    const video = card?.querySelector('video');
    if (!video) return;
    this.#playRequests.delete(video);
    video.pause();
    this.#showPlayback(card, false);
  }

  #release(card) {
    const video = card?.querySelector('video');
    if (!video) return;
    this.#pause(card);
    if (video.hasAttribute('src')) { video.removeAttribute('src'); video.load(); }
  }

  togglePlayback(event) {
    if (Date.now() < this.#suppressClickUntil) return;
    const card = event.target.closest('[data-reel-index]');
    const video = card?.querySelector('video');
    if (!video) return;
    this.#pauseRotation();
    this.refs.status.textContent = '';
    if (card === this.#activeCard && !video.paused) {
      this.#requestedByUser.delete(card);
      this.#pausedByUser.add(card);
      this.#pause(card);
    } else {
      // Normalize the selected physical copy to its original after scrolling.
      const original = this.refs.cards[Number(card.dataset.reelIndex)];
      this.#pausedByUser.delete(original);
      this.#requestedByUser.add(original);
      this.#pausedByUser.delete(card);
      this.#requestedByUser.add(card);
      this.#scrollToPhysical(this.#cards.indexOf(card));
      if (card === this.#activeCard) this.#play(card);
    }
  }

  toggleSound(event) {
    if (Date.now() < this.#suppressClickUntil) return;
    const card = event.target.closest('[data-reel-index]');
    const video = card?.querySelector('video');
    if (!video) return;
    this.#pauseRotation();
    if (card !== this.#activeCard) this.#scrollToPhysical(this.#cards.indexOf(card));
    const muted = !video.muted;
    const original = this.refs.cards[Number(card.dataset.reelIndex)];
    [card, original].forEach((item) => {
      const media = item.querySelector('video');
      if (media) media.muted = muted;
      const button = item.querySelector('.psr__sound');
      if (!button) return;
      button.setAttribute('aria-pressed', String(!muted));
      button.setAttribute('aria-label', muted ? this.dataset.soundOnLabel : this.dataset.soundOffLabel);
    });
  }

  changeVariant(event) {
    this.#pauseRotation();
    const form = event.target.closest('form');
    const product = form?.closest('[data-product-id]');
    if (!product) return;
    // Keep original and loop copies in sync, so a wrap preserves this choice.
    this.querySelectorAll('[data-product-id]').forEach((item) => {
      if (item.dataset.productId !== product.dataset.productId) return;
      const linkedForm = item.querySelector('form');
      const picker = linkedForm.querySelector('[data-variant]');
      picker.value = event.target.value;
      this.#updateVariant(linkedForm);
    });
  }

  // One reusable, native modal; selectors on carousel cards remain inert data.
  #openVariantDialog(form, opener) {
    const dialog = this.refs.variantDialog;
    if (dialog.open) return;
    const product = form.closest('.psr__product').cloneNode(true);
    const copiedIds = new Map();
    for (const element of [product, ...product.querySelectorAll('*')]) {
      const id = element.getAttribute('id');
      if (id) {
        copiedIds.set(id, `${id}-size-popup`);
        element.setAttribute('id', `${id}-size-popup`);
      }
      element.removeAttribute('ref');
      element.removeAttribute('tabindex');
    }
    product.querySelectorAll('[for]').forEach((label) => {
      const id = copiedIds.get(label.getAttribute('for'));
      if (id) label.setAttribute('for', id);
    });
    const popupForm = product.querySelector('form');
    popupForm.dataset.variantConfirm = 'true';
    const picker = popupForm.querySelector('[data-variant]');
    picker.value = form.querySelector('[data-variant]').value;
    picker.setAttribute('aria-label', this.dataset.chooseSizeLabel);
    product.querySelector('.psr__variant-label').textContent = this.dataset.chooseSizeLabel;
    this.refs.variantBody.replaceChildren(product);
    this.refs.variantStatus.textContent = '';
    this.#updateVariant(popupForm);
    this.#variantOpener = opener;
    this.#pauseRotation();
    this.#pause(this.#activeCard);
    lockScroll(this);
    dialog.showModal();
    picker.focus({ preventScroll: true });
  }

  closeVariantDialog() {
    const dialog = this.refs.variantDialog;
    if (dialog?.open) dialog.close();
    this.#variantClosed();
  }

  #variantClosed = () => {
    if (this.refs.variantDialog?.open) return;
    unlockScroll(this);
    this.refs.variantBody?.replaceChildren();
    const opener = this.#variantOpener;
    this.#variantOpener = undefined;
    if (this.isConnected && opener?.isConnected) opener.focus({ preventScroll: true });
  };

  #variantBackdrop = (event) => {
    if (event.target !== this.refs.variantDialog) return;
    const rect = this.refs.variantDialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeVariantDialog();
  };

  #updateVariant(form) {
    const picker = form.querySelector('[data-variant]');
    const option = picker.selectedOptions ? picker.selectedOptions[0] : picker;
    if (!option) return;
    const available = option.dataset.available === 'true';
    const product = form.closest('.psr__product');
    const quantity = Math.max(1, Number(option.dataset.quantity) || 1);
    form.querySelector('[name="quantity"]').value = String(quantity);
    const quantityNote = form.querySelector('[data-quantity-note]');
    quantityNote.hidden = quantity === 1;
    quantityNote.querySelector('span').textContent = String(quantity);
    if (option.dataset.price) product.querySelector('[data-price]').textContent = option.dataset.price;
    if (picker.selectedOptions) {
      const compare = product.querySelector('[data-compare-price]');
      compare.textContent = option.dataset.comparePrice || '';
      compare.hidden = !option.dataset.comparePrice;
      const image = product.querySelector('.psr__product-image img');
      if (image && option.dataset.image && image.getAttribute('src') !== option.dataset.image) {
        image.removeAttribute('srcset');
        image.src = option.dataset.image;
      }
    }
    const button = form.querySelector('[data-reel-buy]');
    const canChoose = picker.selectedOptions && form.dataset.variantConfirm !== 'true';
    const canBuy = canChoose ? [...picker.options].some((item) => item.dataset.available === 'true') : available;
    button.disabled = this.#busy || !canBuy || (this.dataset.purchaseMode !== 'cart' && this.dataset.checkoutEnabled !== 'true');
    button.textContent = this.#busy ? this.dataset.processingLabel : canBuy ? this.dataset.buyLabel : this.dataset.soldOutLabel;
  }

  #setBusy(busy) {
    this.#busy = busy;
    this.querySelectorAll('.psr__form').forEach((form) => {
      form.setAttribute('aria-busy', String(busy));
      const select = form.querySelector('select');
      if (select) select.disabled = busy;
      this.#updateVariant(form);
    });
  }

  #waitForCheckout(signal) {
    return new Promise((resolve, reject) => {
      let timer;
      const deadline = Date.now() + 5000;
      const finish = (error) => {
        clearTimeout(timer);
        signal.removeEventListener('abort', abort);
        window.removeEventListener('gokwikLoaded', check);
        if (error) reject(error); else resolve();
      };
      const abort = () => finish(new DOMException('Disconnected', 'AbortError'));
      const check = () => {
        clearTimeout(timer);
        if (signal.aborted) { abort(); return; }
        if (typeof window.onBuyNowClick === 'function') { finish(); return; }
        if (Date.now() >= deadline) { finish(new Error('Checkout unavailable')); return; }
        timer = setTimeout(check, 100);
      };
      signal.addEventListener('abort', abort, { once: true });
      window.addEventListener('gokwikLoaded', check);
      check();
    });
  }

  async buyNow(event) {
    event.preventDefault();
    const form = event.submitter?.closest('form') || event.target.closest('form') || event.composedPath?.().find((node) => node.tagName === 'FORM');
    if (!form || !this.contains(form) || checkoutInFlight) return;
    const picker = form.querySelector('[data-variant]');
    if (picker.selectedOptions && form.dataset.variantConfirm !== 'true') {
      this.#openVariantDialog(form, event.submitter || form.querySelector('[data-reel-buy]'));
      return;
    }
    const option = picker.selectedOptions ? picker.selectedOptions[0] : picker;
    if (option.dataset.available !== 'true') return;
    if (this.dataset.purchaseMode === 'cart') {
      await this.#addToCart(form, picker, option);
      return;
    }
    if (this.dataset.checkoutEnabled !== 'true' || window.gkBtnConfig?.buyNowEnabled === false) {
      this.refs.status.textContent = this.dataset.checkoutDisabled;
      return;
    }
    const id = picker.value;
    const quantity = Math.max(1, Number(option.dataset.quantity) || 1);
    if (!/^\d+$/.test(id)) return;
    this.#pauseRotation();
    checkoutInFlight = true;
    this.#setBusy(true);
    this.refs.status.textContent = this.dataset.processingLabel;
    this.#pausedByUser.add(this.#activeCard);
    this.#requestedByUser.delete(this.#activeCard);
    this.#pause(this.#activeCard);
    this.#checkoutAbort = new AbortController();
    try {
      await this.#waitForCheckout(this.#checkoutAbort.signal);
      if (!this.isConnected || this.#checkoutAbort.signal.aborted) return;
      // Release the native top layer before GoKwik presents its own checkout.
      if (form.dataset.variantConfirm === 'true') this.closeVariantDialog();
      // Reuse the same SDK contract already verified by snippets/gokwik.liquid.
      // No extra cart item, fee product, checkout URL, or guessed SDK options.
      await Promise.resolve(window.onBuyNowClick(event, false, [{ id, quantity }]));
      if (!this.isConnected) return;
      this.refs.status.textContent = '';
      this.#checkoutTimer = setTimeout(() => {
        this.#checkoutTimer = 0;
        checkoutInFlight = false;
        this.#setBusy(false);
      }, 1500);
    } catch (error) {
      checkoutInFlight = false;
      if (!this.isConnected || error.name === 'AbortError') return;
      this.#setBusy(false);
      this.refs.status.textContent = this.dataset.checkoutError;
      if (this.refs.variantDialog.open) this.refs.variantStatus.textContent = this.dataset.checkoutError;
    }
  }

  async #addToCart(form, picker, option) {
    const id = picker.value;
    if (!/^\d+$/.test(id)) return;
    const quantity = Math.max(1, Number(option.dataset.quantity) || 1);
    this.#pauseRotation();
    checkoutInFlight = true;
    this.#setBusy(true);
    this.refs.status.textContent = this.dataset.processingLabel;
    this.#pause(this.#activeCard);
    this.#checkoutAbort = new AbortController();
    const { signal } = this.#checkoutAbort;
    const root = window.Shopify?.routes?.root || '/';
    const sectionIds = [...new Set(['cart-drawer-section', ...Array.from(document.querySelectorAll('cart-items-component'), (item) => item.dataset.sectionId).filter(Boolean)])].slice(0, 5);
    const body = new FormData(form);
    body.set('id', id);
    body.set('quantity', String(quantity));
    body.set('sections', sectionIds.join(','));
    body.set('sections_url', window.location.pathname);
    let added;
    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || `${root}cart/add.js`, {
        method: 'POST', headers: { Accept: 'application/json' }, body, credentials: 'same-origin', signal,
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || this.dataset.checkoutError);
      added = result;
      // Refresh the cart count as well as the bundled drawer HTML. A failed read
      // never repeats the successful POST or invites another accidental add.
      let cart = null;
      try {
        const cartResponse = await fetch(`${window.Theme?.routes?.cart_url || `${root}cart`}.js`, {
          headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store', signal,
        });
        if (cartResponse.ok) cart = await cartResponse.json();
      } catch (error) {
        if (error.name === 'AbortError') throw error;
      }
      if (!this.isConnected || signal.aborted) return;
      // Restore the size popup's focus before the existing drawer captures it.
      if (form.dataset.variantConfirm === 'true') this.closeVariantDialog();
      const update = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add', context: 'product', lines: [{ merchandiseId: id, quantity }], promise: update.promise,
      }));
      update.resolve({
        cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null,
        detail: { source: 'shoppable-reels-component', sourceId: this.id, productId: form.closest('[data-product-id]')?.dataset.productId, itemCount: quantity, items: cart?.items || result.items || [result], sections: result.sections, didError: false },
      });
      // Let native cart listeners hydrate their rows before revealing the drawer.
      await new Promise(requestAnimationFrame);
      const drawer = document.querySelector('#cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
      else window.location.assign(window.Theme?.routes?.cart_url || `${root}cart`);
      this.refs.status.textContent = this.dataset.addedLabel;
    } catch (error) {
      if (!this.isConnected || error.name === 'AbortError') return;
      const message = added ? this.dataset.addedLabel : error.message || this.dataset.checkoutError;
      this.refs.status.textContent = message;
      if (this.refs.variantDialog.open) this.refs.variantStatus.textContent = message;
    } finally {
      checkoutInFlight = false;
      if (this.isConnected) this.#setBusy(false);
    }
  }
}

if (!customElements.get('shoppable-reels-component')) {
  customElements.define('shoppable-reels-component', ShoppableReels);
}
